<ph-page-header *ngIf="!embedded">
    <ph-button slot="back"
               variant="secondary"
               mode="stroke"
               icon-only
               full-radius
               icon="arrow-left"
               [attr.aria-label]="'Back to ' + returnLabel"
               [routerLink]="[returnRoute]"></ph-button>
    <ph-skeleton *ngIf="vendorId && isVendorLoading; else headerTitle" lines="1" size="lg" width="narrow"></ph-skeleton>
    <ng-template #headerTitle>{{ vendor ? 'Edit vendor' : 'Add new vendor' }}</ng-template>
</ph-page-header>

<ng-container *ngIf="isVendorLoading || isOrgPrefLoading; else content">
    <ph-columns min="360px">
        <ph-stack>
            <ph-card heading="General information">
                <ph-skeleton lines="6" stagger></ph-skeleton>
            </ph-card>
            <ph-card *ngIf="vendorId" heading="Custom fields">
                <ph-skeleton lines="4"></ph-skeleton>
            </ph-card>
        </ph-stack>
        <ph-stack>
            <ph-card heading="Additional details">
                <ph-skeleton lines="5" stagger></ph-skeleton>
            </ph-card>
            <ph-card heading="Logo">
                <ph-skeleton lines="2"></ph-skeleton>
            </ph-card>
        </ph-stack>
    </ph-columns>
</ng-container>

<ng-template #content>

    <form [formGroup]="form">
        <ph-stack>
            <ph-columns min="360px">
                <ph-stack>
                    <ph-card heading="General information">
                        <ph-field>
                            <ph-label required>Vendor name</ph-label>
                            <ph-input
                                ngDefaultControl formControlName="name"
                                placeholder="Vendor name..."
                                required
                                type="text"
                            ></ph-input>
                            <lf-control-messages [control]="form.get('name')"></lf-control-messages>
                        </ph-field>
                        <ph-field>
                            <ph-label>Nickname</ph-label>
                            <ph-input
                                ngDefaultControl formControlName="nickname"
                                placeholder="Nickname..."
                                type="text"
                            ></ph-input>
                        </ph-field>
                        <ph-field>
                            <ph-label>Account number</ph-label>
                            <ph-input
                                ngDefaultControl formControlName="accountNumber"
                                placeholder="Account number..."
                                type="text"
                            ></ph-input>
                        </ph-field>
                        <ph-field>
                            <ph-label>Vendor EIN</ph-label>
                            <ph-input
                                ngDefaultControl formControlName="ein"
                                mask="00-0000000"
                                inputmode="numeric"
                                placeholder="12-3456789"
                                type="text"
                            ></ph-input>
                        </ph-field>
                        <ph-field>
                            <ph-label>
                                Default category
                                <ph-tooltip text="The default category to use for payments made to this vendor"><ph-icon name="info" gap="before"></ph-icon></ph-tooltip>
                            </ph-label>
                            <app-category-picker [formGroup]="form"
                                                 controlName="category"
                                                 [organizationId]="jwt?.orgId"
                                                 [showRevenueCategories]="true"
                                                 [showEquities]="false"
                                                 [showLiabilities]="false"
                                                 [showAssets]="false"></app-category-picker>

                            <lf-control-messages [control]="form.controls['category']"></lf-control-messages>
                        </ph-field>
                        <ph-field>
                            <ph-label>Department / attention / mail stop code</ph-label>
                            <ph-input
                                ngDefaultControl formControlName="mailStopCode"
                                placeholder="Additional recipient information or mail stop code..."
                                type="text"
                            ></ph-input>
                            <lf-control-messages [control]="form.get('mailStopCode')"></lf-control-messages>
                        </ph-field>
                        <ph-field>
                            <ph-label required>Address 1</ph-label>
                            <ph-input
                                ngDefaultControl formControlName="address1"
                                placeholder="Address..."
                                type="text"
                            ></ph-input>
                            <lf-control-messages [control]="form.get('address1')"></lf-control-messages>
                        </ph-field>
                        <ph-field>
                            <ph-label>Address 2</ph-label>
                            <ph-input
                                ngDefaultControl formControlName="address2"
                                placeholder="Address 2..."
                                type="text"
                            ></ph-input>
                            <lf-control-messages [control]="form.get('address2')"></lf-control-messages>
                        </ph-field>
                        <ph-field>
                            <ph-label required>City</ph-label>
                            <ph-input
                                ngDefaultControl formControlName="city"
                                placeholder="City..."
                                required
                                type="text"
                            ></ph-input>
                            <app-required-message *ngIf="isFieldInvalid('city')"></app-required-message>
                        </ph-field>
                        <ph-columns min="140px">
                            <ph-field>
                                <ph-label required>State / province</ph-label>
                                <lf-geo-region-selector
                                    formControlName="region"
                                    ngDefaultControl
                                ></lf-geo-region-selector>
                                <app-required-message *ngIf="isFieldInvalid('region')"></app-required-message>
                            </ph-field>
                            <ph-field>
                                <ph-label required>Zip code</ph-label>
                                <ph-input
                                    ngDefaultControl formControlName="zip"
                                    placeholder="Zip code..."
                                    required
                                    type="text"
                                ></ph-input>
                                <app-required-message *ngIf="isFieldInvalid('zip')"></app-required-message>
                            </ph-field>
                        </ph-columns>
                    </ph-card>

                    <ph-card *ngIf="hasTreasuryEnabled && !vendorId && !embedded"
                             heading="ACH payments"
                             subheading-wrap
                             subheading="Pay this vendor directly from your Treasury Balance by electronic bank transfer. You can also set this up later from the vendor page.">
                        <ph-switch slot="actions"
                                   ngDefaultControl formControlName="enableAch"
                                   aria-label="Set up ACH payment details now"
                                   [disabled]="isFormSubmitting"></ph-switch>

                        <ng-container formGroupName="achDetails" *ngIf="isAchEnabled">
                            <ph-field>
                                <ph-label required>Account holder name</ph-label>
                                <ph-input
                                    ngDefaultControl formControlName="accountHolderName"
                                    placeholder="Name on the account"
                                    required
                                ></ph-input>
                                <lf-control-messages [control]="achDetails.get('accountHolderName')"></lf-control-messages>
                            </ph-field>
                            <ph-field>
                                <ph-label required>Account type</ph-label>
                                <ph-select ngDefaultControl formControlName="accountType" [items]="accountTypeItems"></ph-select>
                            </ph-field>
                            <ph-field>
                                <ph-label required>Routing number</ph-label>
                                <ph-input
                                    ngDefaultControl formControlName="routingNumber"
                                    inputmode="numeric"
                                    placeholder="9-digit routing number"
                                    required
                                ></ph-input>
                                <lf-control-messages [control]="achDetails.get('routingNumber')"></lf-control-messages>
                            </ph-field>
                            <ph-field>
                                <ph-label required>Account number</ph-label>
                                <ph-input
                                    ngDefaultControl formControlName="accountNumber"
                                    inputmode="numeric"
                                    placeholder="Account number"
                                    required
                                ></ph-input>
                                <lf-control-messages [control]="achDetails.get('accountNumber')"></lf-control-messages>
                            </ph-field>
                            <ph-field>
                                <ph-label required>Confirm account number</ph-label>
                                <ph-input
                                    ngDefaultControl formControlName="confirmAccountNumber"
                                    inputmode="numeric"
                                    placeholder="Re-enter account number"
                                    required
                                ></ph-input>
                                <lf-control-messages [control]="achDetails.get('confirmAccountNumber')"></lf-control-messages>
                            </ph-field>
                        </ng-container>
                    </ph-card>

                    <ph-card *ngIf="vendorId && !isVendorLoading" heading="Custom fields">
                        <ph-skeleton *ngIf="isCustomFieldsLoading" lines="2"></ph-skeleton>

                        <ph-columns *ngIf="!isCustomFieldsLoading && customFields.length" min="200px">
                            <ph-field *ngFor="let field of customFields; trackBy: trackByCustomFieldId">
                                <ph-label>{{field.name}}</ph-label>
                                <ph-input [(ngModel)]="field.fieldValue.value" ngDefaultControl
                                          [ngModelOptions]="{standalone: true}"
                                          type="text"></ph-input>
                            </ph-field>
                        </ph-columns>

                        <ph-hint *ngIf="!isCustomFieldsLoading && !customFields.length">
                            No custom fields have been configured for vendors.
                            <ph-link-button [routerLink]="['/app/settings/organization/manage-custom-fields']"
                                            trailing-icon="arrow-square-out">Manage custom fields</ph-link-button>
                        </ph-hint>
                    </ph-card>
                </ph-stack>

                <ph-stack>
                    <ph-card heading="Additional details">
                        <ph-field>
                            <ph-label>Contact name</ph-label>
                            <ph-input
                                ngDefaultControl formControlName="contactName"
                                placeholder="Contact Name"
                                type="text"
                            ></ph-input>
                        </ph-field>
                        <ph-field>
                            <ph-label>Contact email address</ph-label>
                            <ph-input
                                ngDefaultControl formControlName="email"
                                placeholder="Email address"
                                type="text"
                            ></ph-input>
                        </ph-field>
                        <ph-field>
                            <ph-label>Vendor website</ph-label>
                            <ph-input
                                ngDefaultControl formControlName="website"
                                placeholder="Website"
                                type="text"
                            ></ph-input>
                        </ph-field>
                        <ph-columns min="140px">
                            <ph-field>
                                <ph-label>Phone number</ph-label>
                                <ph-input
                                    ngDefaultControl formControlName="phone"
                                    mask="000-000-0000"
                                    inputmode="tel"
                                    placeholder="555-555-5555"
                                    type="text"
                                ></ph-input>
                            </ph-field>
                            <ph-field>
                                <ph-label>Ext.</ph-label>
                                <ph-input
                                    ngDefaultControl formControlName="phoneExt"
                                    placeholder="Ext."
                                    type="text"
                                ></ph-input>
                            </ph-field>
                            <ph-field>
                                <ph-label>Fax number</ph-label>
                                <ph-input
                                    ngDefaultControl formControlName="fax"
                                    mask="000-000-0000"
                                    inputmode="tel"
                                    placeholder="555-555-5555"
                                    type="text"
                                ></ph-input>
                            </ph-field>
                        </ph-columns>
                        <ph-field>
                            <ph-field inline>
                                <ph-switch
                                    ngDefaultControl formControlName="is1099Eligible"
                                    [disabled]="isFormSubmitting"
                                ></ph-switch>
                                <ph-label>1099 eligible?</ph-label>
                            </ph-field>
                        </ph-field>
                    </ph-card>

                    <ph-card heading="Logo">
                        <ph-stack *ngIf="vendorLogoPreview; else vendorLogoDrop" gap="snug" align="start">
                            <ph-file-thumbnail [src]="vendorLogoPreview"
                                               alt="Vendor logo"
                                               size="lg"
                                               [noRemove]="true"></ph-file-thumbnail>
                            <ph-actions align="start">
                                <ph-button variant="secondary"
                                           mode="filled"
                                           size="small"
                                           type="button"
                                           icon="upload-simple"
                                           (click)="openLogoDialog()">Change logo</ph-button>
                                <ph-button variant="error"
                                           mode="stroke"
                                           size="small"
                                           type="button"
                                           icon="trash"
                                           [disabled]="isVendorLogoDeleteInFlight"
                                           (click)="removeVendorLogo()">Remove</ph-button>
                            </ph-actions>
                        </ph-stack>
                        <ng-template #vendorLogoDrop>
                            <ph-file-upload accept="image/png,image/jpeg,image/gif,image/webp"
                                            hint="Recommended image size: 325 x 194px. Supported file types: PNG, JPG, GIF, WEBP."
                                            (ph-files)="openLogoDialog($event.detail?.[0])"></ph-file-upload>
                        </ng-template>
                    </ph-card>

                    <app-organization-logo-dialog *ngIf="isLogoDialogOpen"
                                                  heading="Vendor logo"
                                                  [open]="isLogoDialogOpen"
                                                  [maxWidth]="325"
                                                  [maxHeight]="194"
                                                  [initialFile]="pickedLogoFile"
                                                  (save)="onVendorLogoPicked($event)"
                                                  (closed)="closeLogoDialog()"></app-organization-logo-dialog>
                </ph-stack>
            </ph-columns>

            <ph-actions *ngIf="!embedded">
                <ph-button variant="secondary" mode="stroke" (click)="cancel()">Cancel</ph-button>
                <ph-button [disabled]="isFormSubmitting" type="submit" (click)="onSubmit()">Save vendor</ph-button>
            </ph-actions>
        </ph-stack>
    </form>
</ng-template>
